import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import AddButton from '../../components/ui/AddButton/AddButton'
import AddClubModal from '../../components/ui/AddClubModal/AddClubModal'
import SimpleDeleteModal from '../../components/ui/SimpleDeleteModal/SimpleDeleteModal'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import useHistoryPush from '../../hooks/useHistoryPush'
import SearchField from '../../components/ui/SearchField/SearchField'
import getOrgClubs from '../../Apis/clubs/getOrgClubs'
import createOrgClub from '../../Apis/clubs/createOrgClub'
import updateOrgClub from '../../Apis/clubs/updateOrgClub'
import deleteOrgClub from '../../Apis/clubs/deleteOrgClub'
import QueryKeys from '../../utils/constants/queryKeys'
import clubsPageStyles from './clubsPage.styles'
import USER_PERMISSIONS from '../../utils/constants/userPermissions'
import IsPermitted from '../../components/IsPermitted/IsPermitted'

const useStyles = makeStyles(clubsPageStyles)

const ClubsPage = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { org: orgId } = useUrlSearchParams(['org']) || {}
  const { redirect } = useHistoryPush()

  const [searchQuery, setSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingClub, setEditingClub] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [clubToDelete, setClubToDelete] = useState(null)

  const { data: clubs, isLoading } = useQuery(
    [QueryKeys.GET_ORG_CLUBS, orgId],
    () => getOrgClubs(orgId),
    { enabled: !!orgId }
  )

  const filteredClubs = clubs?.filter((club) =>
    club.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const createClub = useMutation(
    (data) => createOrgClub({ ...data, orgId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_CLUBS, orgId])
        setShowAddModal(false)
      },
      onError: () => {
        setShowAddModal(false)
      },
    }
  )

  const updateClub = useMutation(
    (data) => updateOrgClub({ ...data, orgId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_CLUBS, orgId])
        setShowAddModal(false)
        setEditingClub(null)
      },
      onError: () => {
        setShowAddModal(false)
        setEditingClub(null)
      },
    }
  )

  const deleteClub = useMutation(
    (data) => deleteOrgClub({ ...data, orgId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_ORG_CLUBS, orgId])
        setShowDeleteModal(false)
        setClubToDelete(null)
      },
      onError: () => {
        setShowDeleteModal(false)
        setClubToDelete(null)
      },
    }
  )

  const handleRowClick = (clubId) => {
    redirect(`/clubs/${clubId}`)
  }

  const handleAddClub = () => {
    setEditingClub(null)
    setShowAddModal(true)
  }

  const handleEditClub = (club) => {
    setEditingClub(club)
    setShowAddModal(true)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingClub(null)
  }

  const handleDeleteClub = (club) => {
    setClubToDelete(club)
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    setClubToDelete(null)
  }

  const onConfirmDelete = () => {
    if (clubToDelete) {
      deleteClub.mutate({ clubId: clubToDelete.id })
    }
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        title={
          <div className={classes.headerContainer}>
            <Typography className={classes.cardTitle} variant='h5'>
              Clubs
            </Typography>
            <IsPermitted permission={USER_PERMISSIONS.SETUP_CMS} orgWide>
              <AddButton label='Add Club' onClick={handleAddClub} filled />
            </IsPermitted>
          </div>
        }
      />
      <CardContent>
        <Divider variant='middle' className={classes.divider} />
        <SearchField
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          reset={() => setSearchQuery('')}
          className={classes.searchField}
        />
        {isLoading ? (
          <LoaderDots />
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Club Name</TableCell>
                  <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClubs && filteredClubs.map((club) => (
                  <TableRow
                    key={club.id}
                    className={classes.clickableRow}
                    onClick={() => handleRowClick(club.id)}
                  >
                    <TableCell>
                      <div className={classes.clubNameCell}>
                        {club.logoUrl && (
                          <img
                            src={club.logoUrl}
                            alt={club.name}
                            className={classes.clubLogo}
                          />
                        )}
                        {club.name}
                      </div>
                    </TableCell>
                    <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>
                      <IsPermitted permission={USER_PERMISSIONS.SETUP_CMS} orgWide>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditClub(club)
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size='small'
                          color='secondary'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteClub(club)
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </IsPermitted>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <AddClubModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        createClub={createClub}
        updateClub={updateClub}
        clubData={editingClub}
      />

      <SimpleDeleteModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        text={`Are you sure you want to delete "${clubToDelete?.name}"?`}
        onConfirm={onConfirmDelete}
        isLoading={deleteClub.isLoading}
      />
    </Card>
  )
}

export default ClubsPage
